(() => {
  const ROOT_SELECTOR = '[data-pianzi-egmh3s]';
  const instances = new WeakMap();

  class PianziGlassHeroV3Slideshow {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-pianzi-egmh3s-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-pianzi-egmh3s-dot]'));
      this.prevButton = root.querySelector('[data-pianzi-egmh3s-prev]');
      this.nextButton = root.querySelector('[data-pianzi-egmh3s-next]');
      this.pauseButton = root.querySelector('[data-pianzi-egmh3s-pause]');
      this.stage = root.querySelector('.pianzi-egmh3s__stage');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplay = root.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.interval = Math.max(3000, Number(root.dataset.interval) || 6000);
      this.videoAutoplay = root.dataset.videoAutoplay === 'true';
      this.videoLoop = root.dataset.videoLoop === 'true';
      this.userPaused = false;
      this.inViewport = true;
      this.timer = null;
      this.startX = 0;
      this.startY = 0;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
    }

    initialize() {
      if (!this.slides.length) return;
      this.prepareVideos();
      this.bindEvents();
      this.observeVisibility();
      this.show(this.index, { restart: false });
      this.startTimer();
    }

    destroy() {
      this.stopTimer();
      this.abortController.abort();
      this.observer?.disconnect();
      this.slides.forEach((slide) => this.pauseVideos(slide));
    }

    bindEvents() {
      this.prevButton?.addEventListener('click', () => this.show(this.index - 1), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.show(this.index + 1), { signal: this.signal });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.show(Number(dot.dataset.pianziEgmh3sDot)), { signal: this.signal });
      });

      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.pauseButton.classList.toggle('is-paused', this.userPaused);
        this.pauseButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
        this.pauseButton.setAttribute('aria-label', this.userPaused ? 'Play slideshow' : 'Pause slideshow');
        if (this.userPaused) this.stopTimer();
        else this.startTimer(true);
      }, { signal: this.signal });

      this.root.addEventListener('mouseenter', () => this.stopTimer(), { signal: this.signal });
      this.root.addEventListener('mouseleave', () => this.startTimer(), { signal: this.signal });
      this.root.addEventListener('focusin', () => this.stopTimer(), { signal: this.signal });
      this.root.addEventListener('focusout', () => this.startTimer(), { signal: this.signal });

      this.stage?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + 1);
        }
        if (event.key === 'Home') {
          event.preventDefault();
          this.show(0);
        }
        if (event.key === 'End') {
          event.preventDefault();
          this.show(this.slides.length - 1);
        }
      }, { signal: this.signal });

      this.root.addEventListener('touchstart', (event) => {
        const touch = event.touches?.[0];
        if (!touch) return;
        this.startX = touch.clientX;
        this.startY = touch.clientY;
      }, { passive: true, signal: this.signal });

      this.root.addEventListener('touchend', (event) => {
        const touch = event.changedTouches?.[0];
        if (!touch) return;
        const deltaX = touch.clientX - this.startX;
        const deltaY = touch.clientY - this.startY;
        if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.show(this.index + (deltaX < 0 ? 1 : -1));
        }
      }, { passive: true, signal: this.signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopTimer();
          this.slides.forEach((slide) => this.pauseVideos(slide));
        } else {
          this.playActiveVideo();
          this.startTimer(true);
        }
      }, { signal: this.signal });

      const onBreakpointChange = () => this.playActiveVideo();
      if (typeof this.mobileQuery.addEventListener === 'function') {
        this.mobileQuery.addEventListener('change', onBreakpointChange, { signal: this.signal });
      } else {
        this.mobileQuery.addListener(onBreakpointChange);
        this.signal.addEventListener('abort', () => this.mobileQuery.removeListener(onBreakpointChange), { once: true });
      }

      if (window.Shopify?.designMode) {
        this.root.addEventListener('shopify:block:select', (event) => {
          const slide = event.target.closest('[data-pianzi-egmh3s-slide]');
          if (!slide) return;
          this.userPaused = true;
          this.pauseButton?.classList.add('is-paused');
          this.pauseButton?.setAttribute('aria-pressed', 'true');
          this.stopTimer();
          this.show(Number(slide.dataset.slideIndex), { restart: false });
        }, { signal: this.signal });
      }
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.inViewport = Boolean(entry?.isIntersecting && entry.intersectionRatio > 0.12);
        if (this.inViewport) {
          this.playActiveVideo();
          this.startTimer(true);
        } else {
          this.stopTimer();
          this.slides.forEach((slide) => this.pauseVideos(slide));
        }
      }, { threshold: [0, 0.12, 0.4] });
      this.observer.observe(this.root);
    }

    prepareVideos() {
      this.root.querySelectorAll('video').forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.removeAttribute('controls');

        if (this.videoLoop) {
          video.loop = true;
          video.setAttribute('loop', '');
        }

        if (this.videoAutoplay) {
          video.autoplay = true;
          video.setAttribute('autoplay', '');
        }

        ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'].forEach((eventName) => {
          video.addEventListener(eventName, () => {
            if (video.closest('[data-pianzi-egmh3s-slide]')?.classList.contains('is-active')) {
              this.playActiveVideo();
            }
          }, { passive: true, signal: this.signal });
        });
      });
    }

    show(requestedIndex, { restart = true } = {}) {
      if (!this.slides.length) return;
      const nextIndex = (requestedIndex + this.slides.length) % this.slides.length;
      this.index = nextIndex;

      this.slides.forEach((slide, index) => {
        const active = index === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (!active) this.pauseVideos(slide);
      });

      this.dots.forEach((dot, index) => {
        const active = index === nextIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.playActiveVideo();
      if (restart) this.startTimer(true);
    }

    visibleMedia(slide) {
      return slide.querySelector(this.mobileQuery.matches ? '.pianzi-egmh3s__media--mobile' : '.pianzi-egmh3s__media--desktop');
    }

    playActiveVideo() {
      if (!this.videoAutoplay || !this.inViewport || document.hidden) return;
      const activeSlide = this.slides[this.index];
      if (!activeSlide) return;
      const visibleMedia = this.visibleMedia(activeSlide);

      this.slides.forEach((slide) => {
        slide.querySelectorAll('video').forEach((video) => {
          const shouldPlay = slide === activeSlide && visibleMedia?.contains(video);
          if (!shouldPlay) {
            video.pause();
            return;
          }

          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        });
      });
    }

    pauseVideos(slide) {
      slide.querySelectorAll('video').forEach((video) => video.pause());
    }

    startTimer(forceRestart = false) {
      if (!this.autoplay || this.userPaused || !this.inViewport || document.hidden || this.slides.length < 2) return;
      if (forceRestart) this.stopTimer();
      if (this.timer) return;
      this.timer = window.setInterval(() => this.show(this.index + 1, { restart: false }), this.interval);
    }

    stopTimer() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  function initialize(root) {
    if (!root || instances.has(root)) return;
    const instance = new PianziGlassHeroV3Slideshow(root);
    instances.set(root, instance);
    instance.initialize();
  }

  function initializeAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  }

  function destroyAll(scope) {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []));
    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document), { once: true });
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
